import './ResultCard.scss';

import { camelCase } from 'lodash';
import { useMemo, useState } from 'react';
import { Button, Collapse, Spinner } from 'react-bootstrap';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { OrderDetail, OrderResultFinding, ReportType } from '@customTypes/order';
import { camelToTitleCase, snakeToSentenceCase } from '@helpers/string';
import ResultsNegativeSvg from '@statics/images/results-icons/results_negative.svg?react';
import ResultsPGXSvg from '@statics/images/results-icons/results_pgx.svg?react';
import ResultsPositiveSvg from '@statics/images/results-icons/results_positive.svg?react';
import { getOrderResultsById } from '@store/actions/patientPortal';

import { OrderType, ResultOrder } from '../../Results.types';
import { getRiskScoreIcon } from '../../Results.utils';
import { ResultDetails } from '../ResultDetails/ResultDetails';

interface IProps {
  result: ResultOrder;
  // Individual identifier a guardian is opening on behalf of; undefined = own results.
  individual?: string;
}

export const ResultCard = ({ result, individual }: IProps) => {
  const { type, order_id, product_name, results } = result;

  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [headerTitle, HeaderIcon, headerIconClass] = useMemo(() => {
    if (type === OrderType.PGX) {
      return ['Medication Response', ResultsPGXSvg, 'pgx'];
    }
    const resultFinding = results?.[0]?.finding;
    if (type === OrderType.SINGLE_GENE) {
      const singleGeneTitle = resultFinding || OrderResultFinding.POSITIVE;
      // FVT reports a familial variant as detected/not detected rather than positive/negative.
      const isFvt = result.report_type === ReportType.FAMILY_VARIANT_TESTING;
      const title = isFvt
        ? singleGeneTitle === OrderResultFinding.NEGATIVE
          ? 'Not Detected'
          : 'Detected'
        : camelToTitleCase(camelCase(singleGeneTitle));
      return [
        title,
        singleGeneTitle === OrderResultFinding.POSITIVE ? ResultsPositiveSvg : ResultsNegativeSvg,
        singleGeneTitle.toLowerCase(),
      ];
    }
    const riskScoreTitle =
      resultFinding || (type === OrderType.APOE ? OrderResultFinding.NOT_ANALYZED : OrderResultFinding.HIGH_RISK);
    return [snakeToSentenceCase(riskScoreTitle), getRiskScoreIcon(riskScoreTitle), riskScoreTitle.toLowerCase()];
  }, [type, results, result.report_type]);
  const subTitle = useMemo(() => {
    if (result.type === OrderType.PGX) {
      return 'Changes in genes found that may impact how your body processes certain medications.';
    }
    if (!result.results || !result.results.length) return '';
    if (result.results.length === 1) return result.results[0].text;
    return `
      <ul>
        ${result.results.map(result => `<li>${result.text}</li>`).join('')}
      </ul>
    `;
  }, [result]);

  const fetchData = async () => {
    if (detail) return detail;

    let orderDetail: OrderDetail | null = null;

    try {
      setIsLoading(true);
      orderDetail = await getOrderResultsById({ orderId: order_id, expand: false, individual });
      setDetail(orderDetail);
    } catch (err) {
      console.error('Error on fetching order results:', err);
    } finally {
      setIsLoading(false);
    }

    return orderDetail;
  };

  const onToggle = async () => {
    setIsExpanded(!isExpanded);
    await fetchData();
  };

  return (
    <li className='bg-white result-card'>
      <div className='d-flex align-items-center ps-1 pe-2 py-1 result-card-header'>
        <div className='d-flex align-items-center gap-1'>
          <div className={`result-card-header-icon ${headerIconClass}`}>
            <HeaderIcon desc='result icon' width={16} height={16} />
          </div>
          <p className='fw-semibold text-black result-card-header-title'>{headerTitle}</p>
        </div>
      </div>

      <div className='pt-1 px-3 pb-2 result-card-basic-info'>
        <div className='ps-3'>
          <p className='fw-semibold order-title'>{product_name}</p>
          <p className='fs-sm order-subtitle' dangerouslySetInnerHTML={{ __html: subTitle }} />
        </div>
        <Button variant='link' size='sm' onClick={onToggle} className='mt-2 expand-toggle-btn'>
          {isLoading && isExpanded ? (
            <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
          ) : (
            <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} className='me-2' />
          )}
          {isExpanded ? 'Hide details' : 'View details'}
        </Button>
      </div>

      <Collapse mountOnEnter in={isExpanded}>
        <div>
          {!!detail?.result_json?.results && (
            <ResultDetails
              orderType={type}
              productName={detail.product_name}
              results={detail.result_json.results}
              reportType={result.report_type}
            />
          )}
        </div>
      </Collapse>
    </li>
  );
};
